import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { PhoneFrame } from './PhoneFrame';
import './TrustLayerDemo.css';

type Side = 'alex' | 'jordan';
type ChipKind = 'timestamp' | 'delivered' | 'read' | 'record' | 'court';

interface ChatMessage {
  id: number;
  from: Side;
  text: string;
  time: string;
}

interface TrustChip {
  kind: ChipKind;
  title: string;
  detail: string;
}

interface LedgerEntry {
  id: string;
  step: number;
  delay: number;
  time: string;
  code: string;
  detail: string;
}

const MESSAGES: ChatMessage[] = [
  {
    id: 1,
    from: 'alex',
    text: 'Hey — pickup at 4:30 today, right?',
    time: '4:12 PM',
  },
  {
    id: 2,
    from: 'jordan',
    text: "Yep. I'll be at the school gate by 4:25.",
    time: '4:12 PM',
  },
  {
    id: 3,
    from: 'alex',
    text: "Perfect. Her blue jacket is in the bag — she'll want it.",
    time: '4:13 PM',
  },
  {
    id: 4,
    from: 'jordan',
    text: "Got it. I'll text you when we're home.",
    time: '4:13 PM',
  },
  {
    id: 5,
    from: 'alex',
    text: "Thanks, Jordan. Tomorrow's swap is still 8 AM.",
    time: '4:14 PM',
  },
];

const CHIPS: TrustChip[] = [
  {
    kind: 'timestamp',
    title: 'Timestamped · 4:12 PM',
    detail: 'server-locked at send',
  },
  {
    kind: 'delivered',
    title: 'Delivered',
    detail: "Jordan's device · 4:12 PM",
  },
  {
    kind: 'read',
    title: 'Read ✓ 4:13 PM',
    detail: 'receipt logged',
  },
  {
    kind: 'record',
    title: 'Record #4,821 — unalterable',
    detail: 'hash sealed · 16:13',
  },
  {
    kind: 'court',
    title: 'Court-admissible',
    detail: 'chain intact · export ready',
  },
];

const LEDGER: LedgerEntry[] = [
  {
    id: 'sent-1',
    step: 1,
    delay: 0,
    time: '16:12',
    code: 'MESSAGE_SENT',
    detail: 'alex → jordan',
  },
  {
    id: 'stamp-1',
    step: 1,
    delay: 1,
    time: '16:12',
    code: 'TIMESTAMP_LOCKED',
    detail: '±0.2s',
  },
  {
    id: 'sent-2',
    step: 2,
    delay: 0,
    time: '16:12',
    code: 'MESSAGE_SENT',
    detail: 'jordan → alex',
  },
  {
    id: 'delivered-2',
    step: 2,
    delay: 1,
    time: '16:12',
    code: 'DELIVERED',
    detail: 'receipt logged',
  },
  {
    id: 'sent-3',
    step: 3,
    delay: 0,
    time: '16:13',
    code: 'MESSAGE_SENT',
    detail: 'alex → jordan',
  },
  {
    id: 'read-3',
    step: 3,
    delay: 1,
    time: '16:13',
    code: 'MESSAGE_READ',
    detail: 'receipt logged',
  },
  {
    id: 'sent-4',
    step: 4,
    delay: 0,
    time: '16:13',
    code: 'MESSAGE_SENT',
    detail: 'jordan → alex',
  },
  {
    id: 'sealed-4',
    step: 4,
    delay: 1,
    time: '16:13',
    code: 'RECORD_SEALED',
    detail: '#4821',
  },
  {
    id: 'sent-5',
    step: 5,
    delay: 0,
    time: '16:14',
    code: 'MESSAGE_SENT',
    detail: 'alex → jordan',
  },
  {
    id: 'verified-5',
    step: 5,
    delay: 1,
    time: '16:14',
    code: 'CHAIN_VERIFIED',
    detail: '5/5 · court-ready',
  },
];

const TOTAL_STEPS = MESSAGES.length;
const RECORD_BASE = 4817;
const CHIP_TOPS = [110, 198, 286, 374, 462];

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function ChipIcon({ kind }: { kind: ChipKind }) {
  if (kind === 'timestamp') {
    return (
      <svg viewBox='0 0 14 14' aria-hidden='true'>
        <circle cx='7' cy='7' r='5.2' />
        <path d='M7 4.1V7l1.9 1.2' />
      </svg>
    );
  }

  if (kind === 'delivered') {
    return (
      <svg viewBox='0 0 14 14' aria-hidden='true'>
        <path d='m2.2 7.4 2.7 2.7L11.8 3.2' />
      </svg>
    );
  }

  if (kind === 'read') {
    return (
      <svg viewBox='0 0 14 14' aria-hidden='true'>
        <path d='m1.3 7.4 2.5 2.5 5-5.2' />
        <path d='m6.6 9.9 1.2 1.2 5-5.2' />
      </svg>
    );
  }

  if (kind === 'record') {
    return (
      <svg viewBox='0 0 14 14' aria-hidden='true'>
        <path d='M5.4 1.6 4 12.4M10 1.6 8.6 12.4M1.8 5h11M1.4 9.4h11' />
      </svg>
    );
  }

  return (
    <svg viewBox='0 0 14 14' aria-hidden='true'>
      <path d='M1.5 5.4 7 2.4l5.5 3H1.5Z' />
      <path d='M3.4 11.4V6.2M7 11.4V6.2M10.6 11.4V6.2M2 12.2h10' />
    </svg>
  );
}

export function TrustLayerDemo() {
  const [reduced, setReduced] = useState(prefersReducedMotion);
  const [step, setStep] = useState(() =>
    prefersReducedMotion() ? TOTAL_STEPS : 0
  );
  const [chipsShown, setChipsShown] = useState(() =>
    prefersReducedMotion() ? TOTAL_STEPS : 0
  );

  const threadRef = useRef<HTMLDivElement | null>(null);
  const tapeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }

    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event: MediaQueryListEvent) => {
      setReduced(event.matches);
      if (event.matches) {
        setStep(TOTAL_STEPS);
        setChipsShown(TOTAL_STEPS);
      }
    };

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (reduced || step >= TOTAL_STEPS) return;

    const timer = window.setTimeout(
      () => setStep((current) => Math.min(current + 1, TOTAL_STEPS)),
      step === 0 ? 700 : 2000
    );

    return () => window.clearTimeout(timer);
  }, [reduced, step]);

  useEffect(() => {
    if (reduced) {
      setChipsShown(TOTAL_STEPS);
      return;
    }

    if (step === 0) return;

    const timer = window.setTimeout(() => setChipsShown(step), 420);
    return () => window.clearTimeout(timer);
  }, [reduced, step]);

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;

    const timer = window.setTimeout(() => {
      thread.scrollTo({
        top: thread.scrollHeight,
        behavior: reduced ? 'auto' : 'smooth',
      });
    }, 80);

    return () => window.clearTimeout(timer);
  }, [reduced, step]);

  const shownEntries = LEDGER.filter((entry) => entry.step <= step);

  useEffect(() => {
    const tape = tapeRef.current;
    if (!tape) return;

    tape.scrollTo({
      left: tape.scrollWidth,
      behavior: reduced ? 'auto' : 'smooth',
    });
  }, [reduced, shownEntries.length]);

  const shownMessages = MESSAGES.slice(0, step);
  const shownChips = CHIPS.slice(0, chipsShown);
  const recordCount =
    RECORD_BASE + Math.min(chipsShown, Math.max(TOTAL_STEPS - 1, 0));
  const recordLabel = recordCount.toLocaleString('en-US');

  const replay = () => {
    if (reduced) return;
    setChipsShown(0);
    setStep(0);
  };

  return (
    <div className='tl-page'>
      <header className='tl-header'>
        <p className='tl-eyebrow'>Proof of concept 03 · Contextual trust</p>
        <h1 className='tl-title'>
          Trust, attached to <em>the moment</em>.
        </h1>
        <p className='tl-hypothesis'>
          <strong>What this tests:</strong> OFW/TalkingParents sell trust as
          static text; here trust signals attach to the moment they&rsquo;re
          earned — timestamped, unalterable, court-ready.
        </p>
      </header>

      <div className='tl-stage'>
        <div className='tl-phone-zone'>
          <PhoneFrame size='lg' glow label='One thread · every signal attached'>
            <div className='tl-chat'>
              <div className='tl-chat__bar'>
                <div className='tl-chat__avatar' aria-hidden='true'>
                  JM
                </div>
                <div className='tl-chat__meta'>
                  <span className='tl-chat__name'>Jordan M.</span>
                  <span className='tl-chat__sub'>
                    <svg viewBox='0 0 12 12' aria-hidden='true'>
                      <path d='M6 1.2 10 2.6v3c0 2.4-1.6 4.2-4 5.2-2.4-1-4-2.8-4-5.2v-3L6 1.2Z' />
                      <path d='m4.2 5.9 1.3 1.3 2.4-2.4' />
                    </svg>
                    Verified co-parent
                  </span>
                </div>
                <span className='tl-chat__clock'>4:14</span>
              </div>

              <div className='tl-chat__thread' ref={threadRef}>
                <span className='tl-chat__day'>Today · 4:12 PM</span>
                {shownMessages.map((message) => (
                  <div
                    className={`tl-msg tl-msg--${message.from}`}
                    key={message.id}
                  >
                    <div className='tl-msg__body'>
                      <div className='tl-bubble'>{message.text}</div>
                      <span className='tl-msg__time'>{message.time}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className='tl-chat__composer'>
                <span className='tl-chat__field'>Message Jordan…</span>
                <span className='tl-chat__send' aria-hidden='true'>
                  <svg viewBox='0 0 14 14'>
                    <path d='M2 7h9.5M8 3.5 11.5 7 8 10.5' />
                  </svg>
                </span>
              </div>
            </div>
          </PhoneFrame>

          {chipsShown > 0 ? (
            <span
              className='tl-seam'
              key={`seam-${chipsShown}`}
              aria-hidden='true'
            />
          ) : null}

          <div className='tl-chips' role='list' aria-label='Trust signals'>
            {shownChips.map((chip, index) => {
              const age = shownChips.length - 1 - index;

              return (
                <div
                  className={`tl-chip-slot${age === 0 ? ' is-newest' : ''}`}
                  key={chip.kind}
                  role='listitem'
                  style={
                    {
                      top: `${CHIP_TOPS[index]}px`,
                      opacity: Math.max(0.62, 1 - age * 0.09),
                      transform: `translateX(${age * 4}px)`,
                      zIndex: 10 - age,
                    } as CSSProperties
                  }
                >
                  <div
                    className={`tl-chip tl-chip--${chip.kind}${
                      age === 0 ? ' is-newest' : ''
                    }`}
                  >
                    <span className='tl-chip__icon'>
                      <ChipIcon kind={chip.kind} />
                    </span>
                    <span className='tl-chip__text'>
                      <span className='tl-chip__title'>{chip.title}</span>
                      <span className='tl-chip__detail'>{chip.detail}</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <section className='tl-log'>
        <div className='tl-log__head'>
          <div className='tl-log__title'>
            <span className='tl-live' aria-hidden='true' />
            <span className='tl-log__name'>Ship&rsquo;s log</span>
            <span className='tl-log__sub'>audit tape · read-only</span>
          </div>

          <div className='tl-log__actions'>
            <div className='tl-counter' aria-live='polite'>
              <span className='tl-counter__label'>
                Every message on the record —
              </span>
              <span className='tl-counter__line'>
                <span className='tl-counter__value' key={recordLabel}>
                  {recordLabel}
                </span>
                <span className='tl-counter__tail'>and counting</span>
              </span>
            </div>
            {!reduced ? (
              <button
                type='button'
                className='tl-replay'
                onClick={replay}
                disabled={step === 0}
              >
                Replay
              </button>
            ) : null}
          </div>
        </div>

        <div className='tl-tape-shell'>
          <div
            className='tl-tape'
            ref={tapeRef}
            role='log'
            aria-label='Timestamped audit tape'
          >
            <div className='tl-tape__track'>
              {shownEntries.map((entry) => (
                <div
                  className='tl-tape__entry'
                  key={entry.id}
                  style={
                    {
                      '--tl-entry-delay': `${entry.delay * 260}ms`,
                    } as CSSProperties
                  }
                >
                  <span className='tl-tape__time'>{entry.time}</span>
                  <span className='tl-tape__code'>{entry.code}</span>
                  <span className='tl-tape__detail'>{entry.detail}</span>
                </div>
              ))}
              <span className='tl-tape__caret' aria-hidden='true' />
            </div>
          </div>
        </div>

        <p className='tl-note'>
          Scripted demo · signals and record numbers are illustrative.
        </p>
      </section>
    </div>
  );
}
